import {
  LIST_NEWS_PORTAL,
  RESET_NEWS_PORTAL,
} from 'volto-ministerio-publico-federal/constants';

export function normalizePortalType(query: any[]) {
  return (query || []).map((filter: any) => {
    if (filter.i === 'portal_type' && Array.isArray(filter.v)) {
      const types = new Set(filter.v);
      // Se há qualquer referência a News ou Noticia, garante que ambos estão presentes
      if (types.has('News') || types.has('Noticia')) {
        types.add('News');
        types.add('Noticia');
      }
      return {
        ...filter,
        v: Array.from(types),
      };
    }
    return filter;
  });
}

/**
 * O `mpf_noticias_workflow` dá `View` a `Anonymous` também no estado
 * `pending` (convenção herdada do `simple_publication_workflow`): uma notícia
 * aguardando revisão continua acessível por URL, e por isso o catálogo a
 * devolve em buscas de visitante deslogado. Isso é intencional para quem
 * revisa — mas listagem pública mostra só o que está no ar, e essa parte é
 * responsabilidade da consulta, não do workflow.
 */
const PUBLISHED_ONLY = {
  i: 'review_state',
  o: 'plone.app.querystring.operation.selection.any',
  v: ['published'],
};

const hasReviewState = (query: any[]) =>
  (query || []).some((filter: any) => filter?.i === 'review_state');

/**
 * O workflow de notícias é o que cria o estado `pending` visível ao público.
 * Tipos sem workflow (File, por exemplo) não têm `review_state` indexado, e
 * `PUBLISHED_ONLY` os eliminaria — uma Collection de pasta de documentos
 * saía só com a própria pasta. Por isso o filtro só vale para Collections
 * que pedem notícias.
 */
const queriesNews = (query: any[]) =>
  (query || []).some(
    (filter: any) =>
      filter?.i === 'portal_type' &&
      Array.isArray(filter.v) &&
      (filter.v.includes('News') || filter.v.includes('Noticia')),
  );

/**
 * `append` marca a requisição de "carregar mais": o reducer concatena os itens
 * em vez de trocar a página. O middleware de API do Volto espalha as chaves
 * extras da action no `_SUCCESS`, então o flag chega lá junto do `result`.
 */
export function listNews(data, url, { append = false } = {}) {
  const path = `${url}${url.endsWith('/') ? '' : '/'}`;
  // Busca ambos os tipos: News e Noticia, para cobrir migrações que usam qualquer um
  const portalTypes = Array.isArray(data.portal_type)
    ? data.portal_type
    : [data.portal_type];
  const typesSet = new Set(portalTypes);
  if (typesSet.has('News') || typesSet.has('Noticia')) {
    typesSet.add('News');
    typesSet.add('Noticia');
  }

  const incoming = normalizePortalType(data.query || []);

  const query = [
    {
      i: 'portal_type',
      o: 'plone.app.querystring.operation.selection.any',
      v: Array.from(typesSet),
    },
    // Quem chamou pode restringir o estado por conta própria (uma listagem
    // interna de revisão, por exemplo); só entramos quando ninguém disse nada.
    ...(hasReviewState(incoming) ? [] : [PUBLISHED_ONLY]),
    ...incoming,
  ];
  const body = {
    b_size: Number(data.b_size),
    b_start: Number(data.b_start),
    sort_on: data.sort_on,
    sort_order: data.sort_order,
    query,
    limit: 0,
    // `image_field` é obrigatório para o backend popular `image_scales`
    // (imagem via preview_image_link → Image filho). Sem ele, image_scales
    // vem null e os cards não mostram imagem.
    metadata_fields: [
      'image_scales',
      'image_field',
      'effective',
      'Subject',
      'tema',
      'unit_title',
      'head_title',
      'nav_title',
      'description',
    ],
  };
  return {
    type: LIST_NEWS_PORTAL,
    append,
    request: {
      op: 'post',
      path: `${path}@querystring-search`,
      data: body,
    },
  };
}

export function queryByCollection(
  content,
  { b_start = 0 } = {},
  { append = false } = {},
) {
  const collectionQuery = normalizePortalType(content.query || []);

  const body = {
    b_size: Number(content.item_count || 20),
    b_start: Number(b_start),
    sort_on: content.sort_on || 'effective',
    sort_order: content.sort_reversed ? 'descending' : 'ascending',
    // A coleção manda nos próprios critérios; se ela não disse nada sobre
    // estado, vale a mesma regra do `listNews` (ver PUBLISHED_ONLY).
    query:
      hasReviewState(collectionQuery) || !queriesNews(collectionQuery)
        ? collectionQuery
        : [PUBLISHED_ONLY, ...collectionQuery],
    limit: 0,
    metadata_fields: [
      'UID',
      'image_scales',
      'image_field',
      'effective',
      'Subject',
      'tema',
      'unit_title',
      'head_title',
      'nav_title',
      'description',
    ],
  };
  return {
    type: LIST_NEWS_PORTAL,
    append,
    request: {
      op: 'post',
      path: '/@querystring-search',
      data: body,
    },
  };
}

export function resetNewsPortal() {
  return {
    type: RESET_NEWS_PORTAL,
  };
}
